<template>
  <div
    class="rn-service-area service-modern-area py-16" id="service"
    v-if="NOTARIA && NOTARIA.Theme && NOTARIA.Theme.Home.Servicios.Mostrar"
  >
    <v-container>
      <!-- Encabezado con diseño moderno -->
      <v-row class="mb-10">
        <v-col cols="12" :class="[textAlignment === 'left' ? 'text-left' : textAlignment === 'right' ? 'text-right' : 'text-center']">
          <div class="modern-header-wrapper" :style="wrapperStyle">
            <NuxtLink :to="getLocalePath('/servicios')" class="d-inline-block text-decoration-none">
              <h2 class="modern-heading-title" :style="tituloStyle" v-html="NOTARIA.Theme.Home.Servicios.Titulo.Texto">
              </h2>
            </NuxtLink>
            <div class="modern-header-divider" :class="dividerClass" :style="{ background: estiloTituloColor ? 'linear-gradient(90deg, ' + estiloTituloColor + ' 0%, #3949ab 100%)' : 'linear-gradient(90deg, #1a237e 0%, #3949ab 100%)' }"></div>
            <p class="modern-description mt-4" :class="descriptionClass" :style="descripcionStyle" v-if="NOTARIA.Theme.Home.Servicios.Descripcion.Texto" v-html="NOTARIA.Theme.Home.Servicios.Descripcion.Texto">
            </p>
          </div>
        </v-col>
      </v-row>

      <!-- Lista de servicios con tarjetas premium animadas -->
      <v-row class="row-gap-30">
        <v-col
          cols="12"
          sm="6"
          :md="comNumColumnas"
          v-for="(service, i) in serviciosPublicados()"
          :key="service.Slug || i"
          class="d-flex"
        >
          <NuxtLink
            :to="langPath + '/servicios/' + service.Slug + '?from=home'"
            class="modern-service-link w-100"
          >
            <div class="modern-service-card d-flex flex-column fill-height" :style="{ background: tarjetaBackgroundStyle || '' }">
              <!-- Glow aura backdrop on hover -->
              <div class="card-glow-aura" :style="{ background: 'radial-gradient(circle at top right, ' + (estiloTituloColor || '#3949ab') + '15, transparent 60%)' }"></div>
              
              <!-- Icon/Badge container -->
              <div v-if="showIcons" class="icon-container mb-6 d-flex align-center justify-center" :style="{ color: estiloTituloColor || '#3949ab', '--icon-hover-bg': estiloTituloColor || '#3949ab' }">
                <v-icon class="modern-service-icon" large>{{ getServiceIcon(service) }}</v-icon>
              </div>

              <!-- Content -->
              <div class="card-content d-flex flex-column flex-grow-1">
                <h3 class="modern-card-title mb-3" :style="tarjetaTituloStyle">
                  {{ service.Titulo }}
                </h3>
                <p class="modern-card-description flex-grow-1" :style="tarjetaDescripcionStyle">
                  {{ service.Extracto }}
                </p>
              </div>
            </div>
          </NuxtLink>
        </v-col>
      </v-row>

      <!-- Botón de acción con estilo modernizado -->
      <v-row v-if="NOTARIA.Theme.Home.Servicios.ShowButton" class="mt-12">
        <v-col cols="12" class="text-center">
          <v-btn
            class="modern-action-btn"
            @click="navigateTo(getLocalePath('/servicios'))"
            large
            rounded
            depressed
          >
            {{ NOTARIA.Theme.Home.Servicios.Button.Texto }}
            <v-icon right class="ml-2">mdi-arrow-right-circle-outline</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from '../../store/index'
import { storeToRefs } from 'pinia'
import { useUtils } from '#imports';
import { useStyles } from '../../store/styles';

const store = useStore()
const { NOTARIA } = storeToRefs(store)
const { imagenIDtoSRC, $t, getLocalePath } = useUtils()
const style = useStyles();

// List of professional notary icons matched automatically based on title
const getServiceIcon = (service) => {
  if (service && service.Icon && service.Icon.trim() !== '') {
    return service.Icon;
  }
  const title = service ? service.Titulo : '';
  const t = (title || '').toLowerCase();
  if (t.includes('herencia') || t.includes('testamento') || t.includes('sucesion') || t.includes('sucesores') || t.includes('herederos') || t.includes('fallecimiento') || t.includes('declaracion')) {
    return 'mdi-file-sign'; // Inheritance / Will
  }
  if (t.includes('compraventa') || t.includes('vivienda') || t.includes('inmobiliaria') || t.includes('hipoteca') || t.includes('propiedad') || t.includes('piso') || t.includes('casa') || t.includes('finca') || t.includes('obra nueva')) {
    return 'mdi-home-city-outline'; // Property Sales / Mortgages
  }
  if (t.includes('poder') || t.includes('poderes') || t.includes('autorizacion') || t.includes('representacion')) {
    return 'mdi-file-certificate-outline'; // Powers of Attorney
  }
  if (t.includes('empresa') || t.includes('sociedad') || t.includes('sociedades') || t.includes('mercantil') || t.includes('constitu') || t.includes('accionistas') || t.includes('pact')) {
    return 'mdi-office-building-cog'; // Companies / Corporate
  }
  if (t.includes('boda') || t.includes('matrimonio') || t.includes('divorcio') || t.includes('pareja') || t.includes('separacion') || t.includes('capitulac')) {
    return 'mdi-cards-heart-outline'; // Marriage / Family Law
  }
  if (t.includes('acta') || t.includes('actas') || t.includes('notarial') || t.includes('presencia') || t.includes('requerimiento') || t.includes('notifica')) {
    return 'mdi-clipboard-text-outline'; // Notarial Minutes / Acts
  }
  if (t.includes('prestamo') || t.includes('credito') || t.includes('aval') || t.includes('deuda') || t.includes('poliza') || t.includes('póliza')) {
    return 'mdi-cash-multiple'; // Loans / Financial
  }
  if (t.includes('firma') || t.includes('legitimacion') || t.includes('copia') || t.includes('testimonio')) {
    return 'mdi-draw-pen'; // Signature verification
  }
  // Fallback icon
  return 'mdi-shield-check-outline';
};

const showIcons = computed(() => {
  return NOTARIA.value?.Theme?.Home?.Servicios?.ShowIcons !== false;
})

const langPath = computed(() => {
  const firstLang = store.languages && store.languages.length > 0 ? store.languages[0].Language : 'es';
  if (NOTARIA.value?.Languaje == firstLang) return '';
  return '/' + NOTARIA.value?.Languaje;
})

const comNumColumnas = computed(() => {
  const theme = NOTARIA.value?.Theme
  if (!theme) return 4
  const cols = theme.Home?.Servicios?.NumeroColumnas
  const c = Number(cols)
  if (c === 1) return 12
  if (c === 2) return 6
  if (c === 3) return 4
  if (c === 4) return 3
  return 4
})

function serviciosPublicados() {
  return NOTARIA.value?.Servicios.filter(s => s.Publicado && s.MostrarHome) || []
}

// Map styles from custom admin settings
const estiloTituloColor = computed(() => style.ThemeHomeService?.Titulo?.css?.Color)
const estiloTituloShadow = computed(() => {
  const bg = style.ThemeHomeService?.Titulo?.css?.BackgroundColor;
  return (bg && bg !== 'transparent' && bg !== '#00000000' && bg !== '#0000') ? "4px 2px 6px " + bg : "none";
})

const tituloStyle = computed(() => ({
  color: style.ThemeHomeService?.Titulo?.css?.Color ?? '',
  textShadow: estiloTituloShadow.value,
  fontFamily: style.ThemeHomeService?.Titulo?.css?.Font ?? '',
  fontSize: style.ThemeHomeService?.Titulo?.css?.Size ?? '',
  fontWeight: style.ThemeHomeService?.Titulo?.css?.Weight ?? '',
  textAlign: style.ThemeHomeService?.Titulo?.css?.Align ?? ''
}))

const estiloDescripcionShadow = computed(() => {
  const bg = style.ThemeHomeService?.Descripcion?.css?.BackgroundColor;
  return (bg && bg !== 'transparent' && bg !== '#00000000' && bg !== '#0000') ? "4px 2px 6px " + bg : "none";
})

const descripcionStyle = computed(() => ({
  color: style.ThemeHomeService?.Descripcion?.css?.Color ?? '',
  textShadow: estiloDescripcionShadow.value,
  fontFamily: style.ThemeHomeService?.Descripcion?.css?.Font ?? '',
  fontSize: style.ThemeHomeService?.Descripcion?.css?.Size ?? '',
  fontWeight: style.ThemeHomeService?.Descripcion?.css?.Weight ?? '',
  textAlign: style.ThemeHomeService?.Descripcion?.css?.Align ?? ''
}))

const estiloTarjetaTituloShadow = computed(() => {
  const bg = style.ThemeHomeService?.Tarjeta?.Titulo?.css?.BackgroundColor;
  return (bg && bg !== 'transparent' && bg !== '#00000000' && bg !== '#0000') ? "4px 2px 6px " + bg : "none";
})

const tarjetaTituloStyle = computed(() => ({
  color: style.ThemeHomeService?.Tarjeta?.Titulo?.css?.Color ?? '',
  textShadow: estiloTarjetaTituloShadow.value,
  fontFamily: style.ThemeHomeService?.Tarjeta?.Titulo?.css?.Font ?? '',
  fontSize: style.ThemeHomeService?.Tarjeta?.Titulo?.css?.Size ?? '',
  fontWeight: style.ThemeHomeService?.Tarjeta?.Titulo?.css?.Weight ?? '',
  textAlign: style.ThemeHomeService?.Tarjeta?.Titulo?.css?.Align ?? ''
}))

const estiloTarjetaDescripcionShadow = computed(() => {
  const bg = style.ThemeHomeService?.Tarjeta?.Descripcion?.css?.BackgroundColor;
  return (bg && bg !== 'transparent' && bg !== '#00000000' && bg !== '#0000') ? "4px 2px 6px " + bg : "none";
})

const tarjetaDescripcionStyle = computed(() => ({
  color: style.ThemeHomeService?.Tarjeta?.Descripcion?.css?.Color ?? '',
  textShadow: estiloTarjetaDescripcionShadow.value,
  fontFamily: style.ThemeHomeService?.Tarjeta?.Descripcion?.css?.Font ?? '',
  fontSize: style.ThemeHomeService?.Tarjeta?.Descripcion?.css?.Size ?? '',
  fontWeight: style.ThemeHomeService?.Tarjeta?.Descripcion?.css?.Weight ?? '',
  textAlign: style.ThemeHomeService?.Tarjeta?.Descripcion?.css?.Align ?? ''
}))

const estiloButtonColor = computed(() => style.ThemeHomeService?.Button?.css?.Color || '')
const estiloButtonBgColor = computed(() => style.ThemeHomeService?.Button?.css?.BackgroundColor || '')
const estiloButtonFont = computed(() => style.ThemeHomeService?.Button?.css?.Font || '')
const estiloButtonSize = computed(() => style.ThemeHomeService?.Button?.css?.Size || '')
const estiloButtonWeight = computed(() => style.ThemeHomeService?.Button?.css?.Weight || '')

const tarjetaBackgroundStyle = computed(() => {
  const fondo = style.ThemeHomeService?.Tarjeta?.FondoCSS;
  if (fondo && (fondo.BackgroundColor || fondo.BackgroundColorGradient)) {
    if (fondo.BackgroundColorGradient) {
      return "linear-gradient(135deg, " + (fondo.BackgroundColor || '#ffffff') + ", " + fondo.BackgroundColorGradient + ")";
    } else {
      return fondo.BackgroundColor;
    }
  }
  return '';
})

const textAlignment = computed(() => {
  return style.ThemeHomeService?.Titulo?.css?.Align || 'center';
})

const dividerClass = computed(() => {
  if (textAlignment.value === 'left') return 'mr-auto';
  if (textAlignment.value === 'right') return 'ml-auto';
  return 'mx-auto';
})

const descriptionClass = computed(() => {
  if (textAlignment.value === 'left') return 'mr-auto';
  if (textAlignment.value === 'right') return 'ml-auto';
  return 'mx-auto';
})

const wrapperStyle = computed(() => {
  if (textAlignment.value === 'left') return { margin: '0' };
  if (textAlignment.value === 'right') return { margin: '0 0 0 auto' };
  return { margin: '0 auto' };
})
</script>

<style scoped lang="scss">
.service-modern-area {
  position: relative;
  background-color: #fafbfc;
  overflow: hidden;
}

.modern-header-wrapper {
  max-width: 800px;
  margin: 0 auto;
}

.modern-subtitle {
  display: block;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  margin-bottom: 12px;
  opacity: 0.9;
}

.modern-heading-title {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: -0.5px;
  margin-bottom: 0;
}

.modern-header-divider {
  width: 80px;
  height: 4px;
  border-radius: 2px;
  margin-top: 20px;
  margin-bottom: 20px;
}

.modern-description {
  font-size: 1.1rem;
  line-height: 1.7;
  max-width: 600px;
}

.row-gap-30 {
  row-gap: 30px;
}

.modern-service-link {
  text-decoration: none !important;
  color: inherit !important;
}

.modern-service-card {
  position: relative;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 24px;
  padding: 40px 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02);
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  overflow: hidden;
  z-index: 1;

  .card-glow-aura {
    position: absolute;
    inset: 0;
    opacity: 0;
    transition: opacity 0.4s ease;
    z-index: -1;
  }

  .icon-container {
    width: 64px;
    height: 64px;
    background: rgba(0, 0, 0, 0.03);
    border-radius: 16px;
    transition: all 0.4s ease;
    
    .modern-service-icon {
      transition: transform 0.4s ease;
      color: inherit !important;
    }
  }

  .modern-card-title {
    font-size: 1.35rem;
    font-weight: 700;
    line-height: 1.4;
  }

  .modern-card-description {
    font-size: 0.95rem;
    line-height: 1.6;
    color: #64748b;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .card-action {
    opacity: 0.6;
    transition: all 0.3s ease;
    
    .action-text {
      font-size: 0.85rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1px;
    }

    .action-icon {
      transition: transform 0.3s ease;
    }
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
    border-color: rgba(0, 0, 0, 0.12);

    .card-glow-aura {
      opacity: 1;
    }

    .icon-container {
      background: var(--icon-hover-bg, #3949ab) !important;
      color: #ffffff !important;
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
    }

    .modern-service-icon {
      transform: scale(1.1);
    }

    .card-action {
      opacity: 1;
      
      .action-icon {
        transform: translateX(4px);
      }
    }
  }
}

.modern-action-btn {
  color: v-bind(estiloButtonColor) !important;
  background-color: v-bind(estiloButtonBgColor) !important;
  font-family: v-bind(estiloButtonFont) !important;
  font-size: v-bind(estiloButtonSize) !important;
  font-weight: v-bind(estiloButtonWeight) !important;
  border: 2px solid v-bind(estiloButtonBgColor) !important;
  letter-spacing: 0.5px;
  text-transform: none !important;
  padding: 0 32px !important;
  height: 48px !important;
  box-shadow: 0 6px 20px rgba(0,0,0,0.05) !important;
  transition: all 0.3s ease !important;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.1) !important;
  }
}

@media (max-width: 600px) {
  .modern-heading-title {
    font-size: 1.85rem;
  }
  .modern-service-card {
    padding: 30px 20px;
  }
}
</style>
